import { ANALYTICS_EVENT_NAMES, ANALYTICS_EVENTS } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Calendar } from "@loot-os/ui/components/calendar";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { cn } from "@loot-os/ui/lib/utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CalendarIcon, ChevronRight, RefreshCw, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { AnalyticsEventDetail } from "../components/AnalyticsEventDetail";
import { ErrorAlert } from "../components/ErrorAlert";
import { FilterField } from "../components/FilterField";
import { TableMessageRow } from "../components/TableMessageRow";
import {
  DEFAULT_EVENT_LOG_WINDOW,
  EVENT_LOG_WINDOW_LABELS,
  EVENT_LOG_WINDOWS,
  type EventLogRow,
  type EventLogSearch,
  eventLogRange,
  isEventLogWindow,
  localDayStart,
  rowPropertyEntries,
  summarizeEventProperties,
  toDayKey,
} from "../lib/analyticsEventLog";
import { formatDate, formatFullDateTime, formatLogTime, formatShortDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { useTenantRole } from "../lib/useTenantRole";

const PAGE_SIZE = 100;
const ALL = "all";
const COLUMNS = 6;

// What each select shows in its trigger. Base UI renders the raw value there unless the root
// is handed the labels.
const EVENT_ITEMS: Record<string, string> = {
  [ALL]: "Any event",
  ...Object.fromEntries(ANALYTICS_EVENT_NAMES.map((name) => [name, ANALYTICS_EVENTS[name].label])),
};
const SOURCE_ITEMS: Record<string, string> = {
  [ALL]: "Browser and server",
  browser: "Browser: what shoppers did",
  server: "Server: what happened",
};

/** One calendar day read whole, in place of a trailing window. */
function DayPicker({
  id,
  day,
  onChange,
}: {
  id: string;
  day: string | undefined;
  onChange: (day: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = day ? localDayStart(day) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-expanded={open}
            className="w-full justify-start px-2.5 font-normal"
          />
        }
      >
        <CalendarIcon data-icon="inline-start" className="text-muted-foreground" />
        <span className={selected ? undefined : "text-muted-foreground"}>
          {selected ? formatShortDate(selected) : "Pick a day"}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          defaultMonth={selected ?? new Date()}
          selected={selected}
          disabled={{ after: new Date() }}
          onSelect={(date) => {
            onChange(date ? toDayKey(date) : undefined);
            setOpen(false);
          }}
        />
        {selected && (
          <div className="flex justify-end border-t px-3 py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Back to a trailing window
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The environment's raw analytics events as line items, newest first: what the Analytics page
 * counts, one row each with every recorded field. Tenant admins; each page of rows is one read
 * of the analytics table, so the range is a week at most and older rows load on request.
 */
export function AnalyticsEventsPage() {
  const trpc = useTRPC();
  // Bound to this route, so `search: (prev) => …` is this page's search and not every route's.
  const navigate = useNavigate({ from: "/tenants/$tenantId/analytics/events" });
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const search = useSearch({ strict: false }) as EventLogSearch;

  // Id filters are typed into local state and reach the URL (and the query) settled. Each
  // field's effect follows its own settled text and writes its own key, never the URL's and
  // never a neighbor's: a filter set from a row ("Only this shopper") or cleared lands in the
  // URL first, and text that settled before it must not write itself back.
  const [user, setUser] = useState(search.user ?? "");
  const [anon, setAnon] = useState(search.anon ?? "");
  const [session, setSession] = useState(search.session ?? "");
  const settledUser = useDebounced(user, 500);
  const settledAnon = useDebounced(anon, 500);
  const settledSession = useDebounced(session, 500);
  useEffect(() => setUser(search.user ?? ""), [search.user]);
  useEffect(() => setAnon(search.anon ?? ""), [search.anon]);
  useEffect(() => setSession(search.session ?? ""), [search.session]);
  useEffect(() => {
    const user = settledUser.trim() || undefined;
    void navigate({
      to: ".",
      replace: true,
      search: (prev: EventLogSearch) => ({ ...prev, user }),
    });
  }, [settledUser, navigate]);
  useEffect(() => {
    const anon = settledAnon.trim() || undefined;
    void navigate({
      to: ".",
      replace: true,
      search: (prev: EventLogSearch) => ({ ...prev, anon }),
    });
  }, [settledAnon, navigate]);
  useEffect(() => {
    const session = settledSession.trim() || undefined;
    void navigate({
      to: ".",
      replace: true,
      search: (prev: EventLogSearch) => ({ ...prev, session }),
    });
  }, [settledSession, navigate]);

  const setSearch = (patch: Partial<EventLogSearch>) =>
    void navigate({ to: ".", search: (prev: EventLogSearch) => ({ ...prev, ...patch }) });

  // One "now" per range choice, so the query input is stable across renders and pages of one
  // read share a range; Refresh takes a new one.
  const [refreshedAt, setRefreshedAt] = useState(() => new Date());
  const { w, day } = search;
  const range = useMemo(() => eventLogRange({ w, day }, refreshedAt), [w, day, refreshedAt]);

  // The server enforces the role; this keeps the page from asking when the answer is known.
  const { role, can } = useTenantRole();
  const allowed = can("admin");

  const log = useInfiniteQuery(
    trpc.analytics.events.infiniteQueryOptions(
      {
        ...range,
        eventName: search.event,
        source: search.source,
        endUserId: search.user,
        anonymousId: search.anon,
        sessionId: search.session,
        limit: PAGE_SIZE,
      },
      {
        // A member below admin who follows a link here is told so, not shown a refusal.
        enabled: allowed,
        getNextPageParam: (last) => last.nextCursor,
        placeholderData: (previous) => previous,
        // Each fetch is an Athena statement: never one the reader did not ask for.
        refetchOnWindowFocus: false,
        staleTime: Number.POSITIVE_INFINITY,
        retry: false,
      },
    ),
  );
  const rows = log.data?.pages.flatMap((page) => page.events) ?? [];
  const unavailable = log.data?.pages[0]?.available === false;
  const [expanded, setExpanded] = useState<string | null>(null);

  const hasFilter =
    search.event !== undefined ||
    search.source !== undefined ||
    search.user !== undefined ||
    search.anon !== undefined ||
    search.session !== undefined;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Event log</h1>
        <p className="text-sm text-muted-foreground">
          Every analytics event this environment recorded, one row each with all of its fields: what
          the Analytics page counts. Newest first. An event reaches the log a few minutes after it
          happens, up to fifteen on production.
        </p>
      </div>

      {role !== null && !allowed ? (
        <Card>
          <CardHeader>
            <CardTitle>For tenant admins</CardTitle>
            <CardDescription>
              The event log shows each shopper's browser and tab identifiers, so it is limited to
              this tenant's admins. The Analytics page's counts are open to every member.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : unavailable ? (
        <Card>
          <CardHeader>
            <CardTitle>Not available on this stage</CardTitle>
            <CardDescription>
              This stage has no analytics table linked to the dashboard, so there are no raw events
              to read here. The Analytics page's counts are unaffected.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Filter</CardTitle>
              <CardDescription>
                Ids match exactly. A shopper's id follows them across devices; a browser id and a
                tab id exist on browser events only.
              </CardDescription>
              {hasFilter && (
                <CardAction>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearch(CLEARED_FILTERS)}
                  >
                    <X />
                    Clear filters
                  </Button>
                </CardAction>
              )}
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FilterField id="events-window" label="When">
                <Select
                  items={EVENT_LOG_WINDOW_LABELS}
                  value={search.w ?? DEFAULT_EVENT_LOG_WINDOW}
                  disabled={search.day !== undefined}
                  onValueChange={(value) =>
                    setSearch({ w: isEventLogWindow(value) ? value : undefined })
                  }
                >
                  <SelectTrigger id="events-window" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_LOG_WINDOWS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {EVENT_LOG_WINDOW_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField id="events-day" label="Or one day">
                <DayPicker
                  id="events-day"
                  day={search.day}
                  onChange={(next) => setSearch({ day: next })}
                />
              </FilterField>
              <FilterField id="events-event" label="Event">
                <Select
                  items={EVENT_ITEMS}
                  value={search.event ?? ALL}
                  onValueChange={(value) =>
                    setSearch({ event: value === ALL || value === null ? undefined : value })
                  }
                >
                  <SelectTrigger id="events-event" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_ITEMS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField id="events-source" label="Source">
                <Select
                  items={SOURCE_ITEMS}
                  value={search.source ?? ALL}
                  onValueChange={(value) =>
                    setSearch({
                      source: value === "browser" || value === "server" ? value : undefined,
                    })
                  }
                >
                  <SelectTrigger id="events-source" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_ITEMS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField id="events-user" label="Shopper id">
                <Input
                  id="events-user"
                  placeholder="End user id"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  className="font-mono text-xs"
                />
              </FilterField>
              <FilterField id="events-anon" label="Browser id">
                <Input
                  id="events-anon"
                  placeholder="Anonymous id"
                  value={anon}
                  onChange={(e) => setAnon(e.target.value)}
                  className="font-mono text-xs"
                />
              </FilterField>
              <FilterField id="events-session" label="Tab id">
                <Input
                  id="events-session"
                  placeholder="Session id"
                  value={session}
                  onChange={(e) => setSession(e.target.value)}
                  className="font-mono text-xs"
                />
              </FilterField>
            </CardContent>
          </Card>

          {log.error && <ErrorAlert message={log.error.message} />}

          <Card>
            <CardHeader>
              <CardTitle>
                {rows.length} {rows.length === 1 ? "event" : "events"}
                {log.hasNextPage ? " loaded" : ""}
              </CardTitle>
              <CardDescription>
                {formatDate(range.from)} to {formatDate(range.to)}. Click a row for every recorded
                field.
              </CardDescription>
              <CardAction>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={log.isFetching}
                  onClick={() => setRefreshedAt(new Date())}
                >
                  <RefreshCw className={log.isFetching ? "animate-spin" : undefined} />
                  Refresh
                </Button>
              </CardAction>
            </CardHeader>
            {/* A container, so an open row's detail can take the card's width and not the table's. */}
            <CardContent className="@container">
              <Table
                aria-busy={log.isPlaceholderData}
                className={`table-fixed ${log.isPlaceholderData ? "opacity-60 transition-opacity" : ""}`}
              >
                <colgroup>
                  <col className="w-52" />
                  <col className="w-44" />
                  <col className="w-20" />
                  <col className="w-44" />
                  <col className="w-44" />
                  <col />
                </colgroup>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Who</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Properties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {log.isPending && (
                    <TableMessageRow colSpan={COLUMNS}>Reading the event log…</TableMessageRow>
                  )}
                  {/* Pending, not loading: the query waits for the role before it starts. */}
                  {!log.isPending && !log.error && rows.length === 0 && (
                    <TableMessageRow colSpan={COLUMNS}>
                      {hasFilter
                        ? "Nothing matches these filters."
                        : "No events in this range yet."}
                    </TableMessageRow>
                  )}
                  {rows.map((row) => (
                    <EventRow
                      key={row.id}
                      tenantId={tenantId}
                      row={row}
                      isOpen={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      onFilter={setSearch}
                    />
                  ))}
                </TableBody>
              </Table>
              {log.hasNextPage && (
                <div className="mt-4 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={log.isFetchingNextPage}
                    onClick={() => void log.fetchNextPage()}
                  >
                    {log.isFetchingNextPage ? "Loading…" : "Load older events"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const CLEARED_FILTERS: Partial<EventLogSearch> = {
  event: undefined,
  source: undefined,
  user: undefined,
  anon: undefined,
  session: undefined,
};

// The open row and the detail under it share one edge in the primary color, so the pair reads
// as a single block and the eye finds which row the fields belong to.
const OPEN_EDGE = "shadow-[inset_2px_0_0_var(--primary)]";

/**
 * One event on one line, and under it, while open, every field it recorded. The open row keeps
 * a solid fill (the hover tint is half of it, so open and hovered never look alike) and loses
 * its bottom border to the detail row.
 */
function EventRow({
  tenantId,
  row,
  isOpen,
  onToggle,
  onFilter,
}: {
  tenantId: string;
  row: EventLogRow;
  isOpen: boolean;
  onToggle: () => void;
  onFilter: (patch: Partial<EventLogSearch>) => void;
}) {
  const detailId = `event-detail-${row.id}`;
  return (
    <>
      <TableRow
        className={cn(
          "cursor-pointer",
          isOpen && "border-b-0 hover:bg-muted has-aria-expanded:bg-muted",
        )}
        onClick={onToggle}
      >
        <TableCell className={cn("text-muted-foreground tabular-nums", isOpen && OPEN_EDGE)}>
          {/* The keyboard way in. It has no handler of its own: its click bubbles to the row,
              which toggles once. */}
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={detailId}
            title={formatFullDateTime(row.occurredAt)}
            className="flex cursor-pointer items-center gap-2 rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 transition-transform",
                isOpen && "rotate-90 text-primary",
              )}
            />
            <time dateTime={row.occurredAt} className={cn(isOpen && "text-foreground")}>
              {formatLogTime(row.occurredAt)}
            </time>
          </button>
        </TableCell>
        <TableCell className={cn("truncate", isOpen && "font-medium")} title={row.eventName}>
          {row.label}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{row.source}</TableCell>
        <TableCell>
          <WhoCell tenantId={tenantId} row={row} />
        </TableCell>
        <TableCell className="truncate font-mono text-xs" title={row.route ?? undefined}>
          {row.route ?? <span className="text-muted-foreground">—</span>}
        </TableCell>
        <PropertiesCell row={row} />
      </TableRow>
      {isOpen && (
        <TableRow id={detailId} className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={COLUMNS} className={cn("whitespace-normal", OPEN_EDGE)}>
            {/* As wide as the card and held at its left edge: on a window too narrow for the
                columns the table scrolls sideways, and the detail must not scroll off with it. */}
            <div className="sticky left-(--card-spacing) w-[100cqw] max-w-full">
              <AnalyticsEventDetail tenantId={tenantId} row={row} onFilter={onFilter} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** The shopper when the event has one (linked to their page), else the browser that sent it. */
function WhoCell({ tenantId, row }: { tenantId: string; row: EventLogRow }) {
  if (row.endUserId) {
    return (
      <Link
        to="/tenants/$tenantId/users/$userId"
        params={{ tenantId, userId: row.endUserId }}
        // Inline, so the link is as wide as its text: the rest of the cell toggles the row.
        className="inline-block max-w-full truncate align-bottom text-primary hover:underline"
        title={row.endUserId}
        onClick={(e) => e.stopPropagation()}
      >
        {row.endUser?.username ?? row.endUser?.externalId ?? row.endUserId}
      </Link>
    );
  }
  if (row.anonymousId) {
    return (
      <span className="block truncate font-mono text-xs" title={row.anonymousId}>
        {row.anonymousId}
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

/**
 * The properties on one truncated line, each key quieter than its value. A `route` the Route
 * column already shows is left out here; the open row lists every property.
 */
function PropertiesCell({ row }: { row: EventLogRow }) {
  const entries = rowPropertyEntries(row.properties, row.route);
  if (entries === null) {
    return (
      <TableCell className="truncate font-mono text-xs" title={row.properties}>
        {row.properties}
      </TableCell>
    );
  }
  if (entries.length === 0) {
    return <TableCell className="text-muted-foreground">—</TableCell>;
  }
  return (
    <TableCell
      className="truncate font-mono text-xs"
      title={summarizeEventProperties(row.properties)}
    >
      {entries.map((entry) => (
        <span key={entry.key} className="mr-4 last:mr-0">
          <span className="text-muted-foreground">{entry.key}</span> {entry.value}
        </span>
      ))}
    </TableCell>
  );
}
